import { Box, Button } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import { REDACTED_SECRET_PLACEHOLDER } from "../services/configurationApi";
import { ProcessedService } from "../types";
import { FrequencyScheduleModal } from "./FrequencyScheduleModal";

interface ServiceCardProps {
  service: ProcessedService;
  onChange: (appName: string, field: keyof ProcessedService, value: any) => void;
  disabled?: boolean;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  onChange,
  disabled = false,
}) => {
  const [isFrequencyModalOpen, setIsFrequencyModalOpen] = useState(false);
  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(service.app_name, "is_active", event.target.checked);
  };

  const handleConfigChange = (configKey: string, value: string) => {
    const updatedConfigurations = {
      ...(service.configurations || {}),
      [configKey]: value,
    };

    onChange(service.app_name, "configurations", updatedConfigurations);
  };

  const getRedactedFields = () =>
    Array.isArray(service.configurations?._redacted) ? service.configurations._redacted : [];

  const getFieldLabel = (configKey: string) => {
    const labelMap: Record<string, string> = {
      measurement_id: "Measurement ID",
      container_id: "Container ID",
      publisher_id: "Publisher ID",
      site_key: "Site Key",
      api_key: "API Key",
      jwks: "JWKS",
      appId: "App ID",
      token: "Token",
      domain: "Domain",
      saleorApiUrl: "Saleor API URL",
      verification_meta_tag: "Google Site Verification Meta Tag",
    };

    return (
      labelMap[configKey] || configKey.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
    );
  };

  const getPlaceholder = (configKey: string) => {
    if (getRedactedFields().includes(configKey)) {
      return REDACTED_SECRET_PLACEHOLDER;
    }

    const placeholderMap: Record<string, string> = {
      measurement_id: "Enter Analytics Measurement ID",
      container_id: "Enter GTM Container ID",
      publisher_id: "Enter AdSense Publisher ID",
      site_key: "Enter reCAPTCHA Site Key",
      api_key: "Enter API Key",
      jwks: "Enter JWKS configuration",
      appId: "Enter App ID",
      token: "Enter Token",
      domain: "Enter Domain",
      saleorApiUrl: "Enter Saleor API URL",
      verification_meta_tag:
        '<meta name="google-site-verification" content="w9vSRrH7WvnDXQLyerxHX0h6CI4NdsVGeV44MSXZaoM" />',
    };

    return placeholderMap[configKey] || `Enter ${getFieldLabel(configKey)}`;
  };

  const getRequiredConfigFields = (appName: string): string[] => {
    const configFieldsMap: Record<string, string[]> = {
      google_adsense: ["publisher_id"],
      google_maps: ["api_key"],
      google_analytics: ["measurement_id"],
      google_tag_manager: ["container_id"],
      google_search_console: ["verification_meta_tag"],
    };

    return configFieldsMap[appName] || [];
  };

  const handleFrequencyUpdate = (frequencyConfig: any) => {
    // Update the local state but don't save to API yet
    // The API save will happen when user clicks the main "Save" button on the page
    const updatedConfigurations = {
      ...(service.configurations || {}),
      ...frequencyConfig,
    };

    onChange(service.app_name, "configurations", updatedConfigurations);
  };

  return (
    <Box
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "16px 0",
      }}
    >
      <Box style={{ flex: 1 }}>
        <Box
          as="div"
          style={{
            fontSize: "16px",
            fontWeight: 400,
            color: "#202124",
            marginBottom: "4px",
          }}
        >
          {service.display_name}
        </Box>
        <Box
          as="div"
          style={{
            fontSize: "12px",
            color: "#9aa0a6",
            marginBottom: "8px",
          }}
        >
          {service.description}
        </Box>

        {/* Show configuration fields for external apps */}
        {service.type === "external" &&
          service.is_active &&
          (() => {
            const requiredFields = getRequiredConfigFields(service.app_name);
            const currentConfigs = service.configurations || {};

            // Create a complete set of fields including required ones that might be missing
            // Filter out 'locations' (reCAPTCHA) and frequency fields (Google Merchant) as they're handled separately
            const configKeys = Object.keys(currentConfigs).filter(
              key =>
                key !== "_redacted" &&
                key !== "locations" &&
                ![
                  "frequency",
                  "hour",
                  "minute",
                  "timezone",
                  "day_of_week",
                  "day_of_month",
                ].includes(key),
            );
            const allFields = [
              ...new Set([...requiredFields, ...configKeys, ...getRedactedFields()]),
            ];

            return allFields.length > 0 ? (
              <Box style={{ marginTop: "12px" }}>
                {allFields.map(configKey => (
                  <Box key={configKey} style={{ marginBottom: "12px" }}>
                    <Box
                      as="label"
                      style={{
                        fontSize: "12px",
                        color: "#5f6368",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      {getFieldLabel(configKey)}
                    </Box>
                    <input
                      value={currentConfigs[configKey] || ""}
                      onChange={e => handleConfigChange(configKey, e.target.value)}
                      placeholder={getPlaceholder(configKey)}
                      disabled={disabled}
                      name={`${service.app_name}_${configKey}`}
                      data-test-id={`service-input-${service.app_name}-${configKey}`}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #dadce0",
                        borderRadius: "4px",
                        fontSize: "13px",
                        fontFamily: "'Courier New', monospace",
                        backgroundColor: "white",
                        outline: "none",
                      }}
                    />
                  </Box>
                ))}

                {/* Google Search Console Guidance */}
                {service.app_name === "google_search_console" &&
                  service.configurations?.verification_meta_tag && (
                    <Box style={{ marginTop: "16px" }}>
                      <Box
                        style={{
                          backgroundColor: "#e8f0fe",
                          border: "1px solid #1a73e8",
                          borderRadius: "8px",
                          padding: "16px",
                          marginBottom: "12px",
                        }}
                      >
                        <Box
                          as="div"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#1a73e8",
                            marginBottom: "8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span style={{ fontSize: "16px" }}>ℹ️</span>
                          Next Steps for Google Search Console
                        </Box>
                        <Box
                          as="div"
                          style={{
                            fontSize: "13px",
                            color: "#1a73e8",
                            lineHeight: "1.5",
                          }}
                        >
                          <p style={{ margin: "0 0 8px 0" }}>
                            <strong>1.</strong> After saving this configuration, the meta tag will
                            be added to your site&apos;s homepage automatically.
                          </p>
                          <p style={{ margin: "0 0 8px 0" }}>
                            <strong>2.</strong> In Google Search Console, go to{" "}
                            <strong>Sitemaps</strong> section.
                          </p>
                          <p style={{ margin: "0" }}>
                            <strong>3.</strong> Add this sitemap URL:{" "}
                            <code
                              style={{
                                backgroundColor: "white",
                                padding: "2px 6px",
                                borderRadius: "3px",
                                fontFamily: "'Courier New', monospace",
                                border: "1px solid #dadce0",
                              }}
                            >
                              /sitemap.xml
                            </code>{" "}
                            and submit it.
                          </p>
                        </Box>
                      </Box>
                    </Box>
                  )}
              </Box>
            ) : (
              <Box
                as="div"
                style={{
                  fontSize: "12px",
                  color: "#9aa0a6",
                  marginTop: "8px",
                  padding: "8px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                  border: "1px solid #e8eaed",
                }}
              >
                No additional configuration required
              </Box>
            );
          })()}

        {/* Google Merchant Frequency Configuration */}
        {service.app_name === "google_merchant" && service.is_active && (
          <Box style={{ marginTop: "16px" }}>
            <Box
              as="div"
              style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#202124",
                marginBottom: "12px",
              }}
            >
              Feed Generation Schedule
            </Box>
            <Box
              as="div"
              style={{
                fontSize: "12px",
                color: "#5f6368",
                marginBottom: "12px",
              }}
            >
              Configure when the Google Merchant feed file should be generated:
            </Box>

            <Box
              style={{
                border: "1px solid #dadce0",
                borderRadius: "8px",
                padding: "12px",
                backgroundColor: "#f8f9fa",
                marginBottom: "12px",
              }}
            >
              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box style={{ flex: 1 }}>
                  <Box
                    as="div"
                    style={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#202124",
                      marginBottom: "8px",
                    }}
                  >
                    Feed Generation Schedule
                  </Box>

                  {service.configurations?.frequency ? (
                    <Box>
                      <Box
                        as="div"
                        style={{
                          fontSize: "12px",
                          color: "#202124",
                          marginBottom: "4px",
                        }}
                      >
                        <strong>Frequency:</strong>{" "}
                        {service.configurations.frequency.charAt(0).toUpperCase() +
                          service.configurations.frequency.slice(1)}
                      </Box>

                      {service.configurations.frequency === "hourly" && (
                        <>
                          <Box
                            as="div"
                            style={{
                              fontSize: "12px",
                              color: "#5f6368",
                              marginBottom: "2px",
                            }}
                          >
                            <strong>Hours Step:</strong> Every {service.configurations.hour} hour(s)
                          </Box>
                          <Box
                            as="div"
                            style={{
                              fontSize: "12px",
                              color: "#5f6368",
                              marginBottom: "2px",
                            }}
                          >
                            <strong>Minute:</strong>{" "}
                            {service.configurations.minute.toString().padStart(2, "0")}
                          </Box>
                        </>
                      )}

                      {service.configurations.frequency === "daily" && (
                        <>
                          <Box
                            as="div"
                            style={{
                              fontSize: "12px",
                              color: "#5f6368",
                              marginBottom: "2px",
                            }}
                          >
                            <strong>Time:</strong>{" "}
                            {service.configurations.hour.toString().padStart(2, "0")}:
                            {service.configurations.minute.toString().padStart(2, "0")}
                          </Box>
                        </>
                      )}

                      {service.configurations.frequency === "weekly" && (
                        <>
                          <Box
                            as="div"
                            style={{
                              fontSize: "12px",
                              color: "#5f6368",
                              marginBottom: "2px",
                            }}
                          >
                            <strong>Day:</strong>{" "}
                            {(() => {
                              const dayNames = [
                                "Sunday",
                                "Monday",
                                "Tuesday",
                                "Wednesday",
                                "Thursday",
                                "Friday",
                                "Saturday",
                              ];

                              return dayNames[service.configurations.day_of_week];
                            })()}
                          </Box>
                          <Box
                            as="div"
                            style={{
                              fontSize: "12px",
                              color: "#5f6368",
                              marginBottom: "2px",
                            }}
                          >
                            <strong>Time:</strong>{" "}
                            {service.configurations.hour.toString().padStart(2, "0")}:
                            {service.configurations.minute.toString().padStart(2, "0")}
                          </Box>
                        </>
                      )}

                      {service.configurations.frequency === "monthly" && (
                        <>
                          <Box
                            as="div"
                            style={{
                              fontSize: "12px",
                              color: "#5f6368",
                              marginBottom: "2px",
                            }}
                          >
                            <strong>Date:</strong> Day {service.configurations.day_of_month} of each
                            month
                          </Box>
                          <Box
                            as="div"
                            style={{
                              fontSize: "12px",
                              color: "#5f6368",
                              marginBottom: "2px",
                            }}
                          >
                            <strong>Time:</strong>{" "}
                            {service.configurations.hour.toString().padStart(2, "0")}:
                            {service.configurations.minute.toString().padStart(2, "0")}
                          </Box>
                        </>
                      )}

                      <Box
                        as="div"
                        style={{
                          fontSize: "12px",
                          color: "#5f6368",
                          marginTop: "4px",
                        }}
                      >
                        <strong>Timezone:</strong> {service.configurations.timezone}
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      as="div"
                      style={{
                        fontSize: "12px",
                        color: "#9aa0a6",
                        fontStyle: "italic",
                      }}
                    >
                      No schedule configured. Click &quot;Edit&quot; to set up feed generation
                      schedule.
                    </Box>
                  )}
                </Box>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setIsFrequencyModalOpen(true)}
                  disabled={disabled}
                >
                  Edit
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Show read-only configuration info for internal apps */}
        {service.type === "internal" &&
          service.configurations &&
          Object.keys(service.configurations).length > 0 && (
            <Box
              as="div"
              style={{
                fontSize: "11px",
                color: "#9aa0a6",
                fontFamily: "'Courier New', monospace",
                wordBreak: "break-all",
                marginTop: "8px",
                padding: "8px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                border: "1px solid #e8eaed",
              }}
            >
              Internal configuration managed by system
            </Box>
          )}
      </Box>

      <Box
        style={{
          marginLeft: "20px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        <label
          style={{
            position: "relative",
            width: "52px",
            height: "28px",
            display: "inline-block",
          }}
        >
          <input
            type="checkbox"
            checked={service.is_active}
            onChange={handleToggleChange}
            disabled={disabled}
            data-test-id={`service-toggle-${service.app_name}`}
            style={{
              opacity: 0,
              width: 0,
              height: 0,
            }}
          />
          <span
            style={{
              position: "absolute",
              cursor: "pointer",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: service.is_active ? "#1a73e8" : "#dadce0",
              transition: "0.3s",
              borderRadius: "34px",
            }}
          >
            <span
              style={{
                position: "absolute",
                content: '""',
                height: "20px",
                width: "20px",
                left: service.is_active ? "28px" : "4px",
                bottom: "4px",
                backgroundColor: "white",
                transition: "0.3s",
                borderRadius: "50%",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                display: "block",
              }}
            />
          </span>
        </label>
      </Box>

      {/* Frequency Schedule Modal for Google Merchant */}
      {service.app_name === "google_merchant" && (
        <FrequencyScheduleModal
          open={isFrequencyModalOpen}
          onClose={() => setIsFrequencyModalOpen(false)}
          onUpdate={handleFrequencyUpdate}
          initialConfig={{
            frequency: service.configurations?.frequency || "monthly",
            hour: service.configurations?.hour || 12,
            minute: service.configurations?.minute || 0,
            timezone: service.configurations?.timezone || "Asia/Karachi",
            day_of_week: service.configurations?.day_of_week || 1,
            day_of_month: service.configurations?.day_of_month || 1,
          }}
        />
      )}
    </Box>
  );
};
